import { css, cx } from "@emotion/css";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ReactElement } from "react";
import { useIsLargerThanIpadResolution } from "../../hooks";
import { Badge } from "../Badge";
import type { SimpleMenuItem } from "../MenuItems";
import type { PageTitleBadge } from "../Page";
import { PageActions } from "../PageActions";
import type { PageAction, PrimaryPageAction } from "../PageActions";
import { Tooltip } from "../Tooltip";
interface HeaderSecondaryMainWithActionsProps {
    /**
     * An optional primary action to display on the right side of the header
     */
    primaryAction?: PrimaryPageAction;
    /**
     * An optional list of actions to display on the right side of the header
     */
    pageActions?: PageAction[];
    /**
     * The items to show as part of the overflow menu on the right side of the header
     */
    overflowActions?: SimpleMenuItem[];
}
export type HeaderSecondaryMainContentProps = HeaderSecondaryMainWithActionsProps & PageHeaderTitleProps;
export function HeaderSecondaryMainContent({ titleStatusIcon, title, titleChip, titleBadge, titleAccessory, titleComplementaryText, ...actionProps }: HeaderSecondaryMainContentProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return (<div className={cx(mainContentStyles, { [mainContentMobileStyles]: !isLargerThanIpad })}>
            <PageHeaderTitle title={title} titleStatusIcon={titleStatusIcon} titleChip={titleChip} titleBadge={titleBadge} titleAccessory={titleAccessory} titleComplementaryText={titleComplementaryText}/>
            <PageActions primaryAction={actionProps.primaryAction} actions={actionProps.pageActions} overflowActions={actionProps.overflowActions}/>
        </div>);
}
interface PageHeaderTitleProps {
    /** The title of the HeaderSecondary. */
    title: string | undefined;
    /**
     * Additional text to show immediately after the `title` with less emphasis.
     */
    titleComplementaryText?: string;
    /**
     * The status icon to show on the left side of the title.
     *
     * The status icon is only visible when this property is defined.
     */
    titleStatusIcon?: ReactElement;
    /**
     * The badge that shows on the right side of the title.
     */
    titleBadge?: PageTitleBadge;
    /**
     * @deprecated Use titleBadge props instead.
     *
     * The chip to show on the right side of the title, the status icon is only visible when this property is defined.
     */
    titleChip?: ReactElement;
    /**
     * An element which can be used to display in addition to the title. This is
     * displayed to the right of the title, but before any chips.
     */
    titleAccessory?: ReactElement;
}
function PageHeaderTitle({ titleStatusIcon, title, titleAccessory, titleChip, titleBadge, titleComplementaryText }: PageHeaderTitleProps) {
    const isLargerThanIpad = useIsLargerThanIpadResolution();
    return (<div className={cx(titleSectionStyles, { [titleSectionMobileStyles]: !isLargerThanIpad })}>
            <div className={titleLabelStyles}>
                {titleStatusIcon}
                <span>
                    <h1 className={titleStyles}>{title}</h1>
                    {titleComplementaryText && <span className={titleComplementaryTextStyles}> {titleComplementaryText}</span>}
                </span>
            </div>
            {titleAccessory}
            {titleBadge &&
            (titleBadge.tooltipContent ? (<Tooltip content={titleBadge.tooltipContent}>
                        <div className={tooltipBadgeContainer}>
                            <Badge size={"medium"} variant={titleBadge.variant} label={titleBadge.label} icon={titleBadge.icon}/>
                        </div>
                    </Tooltip>) : (<Badge size={"medium"} variant={titleBadge.variant} label={titleBadge.label} icon={titleBadge.icon}/>))}
            <div className={chipAndLoadingIndicatorWrapperStyles}>{titleChip}</div>
        </div>);
}
const mainContentStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: space[24],
    minHeight: "2.25rem",
});
const mainContentMobileStyles = css({
    flexDirection: "column",
    alignItems: "flex-start",
});
const titleLabelStyles = css({
    display: "flex",
    gap: space[12],
    alignItems: "center",
});
const titleSectionStyles = css({
    display: "flex",
    gap: space[8],
    alignItems: "center",
});
const titleSectionMobileStyles = css({
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
});
const titleStyles = css({
    all: "unset",
    font: text.heading.medium,
    color: themeTokens.color.text.primary,
});
const titleComplementaryTextStyles = css({
    font: text.heading.medium,
    color: themeTokens.color.text.tertiary,
});
const chipAndLoadingIndicatorWrapperStyles = css({
    display: "flex",
    alignItems: "center",
    gap: space[12],
});
const tooltipBadgeContainer = css({
    display: "flex",
});
